export interface SchedulePostPaintHovTargetHoverRefreshOptions {
  readonly waitForPaint: () => Promise<void>;
  readonly canRefreshHovTargetHoverIntents: () => boolean;
  readonly refreshHovTargetHoverIntents: () => void;
}

export interface PostPaintHovTargetHoverRefreshScheduler {
  readonly schedule: () => void;
  // Reset clears authoritative hover state, so stale paint work must be
  // replaced by one latest trailing refresh even if presentation stays held.
  readonly rescheduleAfterReset: () => void;
  readonly invalidate: () => void;
}

export function createPostPaintHovTargetHoverRefreshScheduler(
  options: SchedulePostPaintHovTargetHoverRefreshOptions,
): PostPaintHovTargetHoverRefreshScheduler {
  let generation = 0;
  let isPending = false;
  let trailingScheduleRequested = false;

  const startWait = (waitGeneration: number): void => {
    isPending = true;
    void options.waitForPaint().then(() => {
      isPending = false;
      if (
        waitGeneration === generation &&
        options.canRefreshHovTargetHoverIntents()
      ) {
        options.refreshHovTargetHoverIntents();
      }
      if (!trailingScheduleRequested) {
        return;
      }
      trailingScheduleRequested = false;
      startWait(generation);
    });
  };

  return {
    schedule(): void {
      if (!options.canRefreshHovTargetHoverIntents()) {
        return;
      }

      generation += 1;
      if (isPending) {
        trailingScheduleRequested = true;
        return;
      }
      startWait(generation);
    },

    rescheduleAfterReset(): void {
      generation += 1;
      trailingScheduleRequested = false;
      if (!options.canRefreshHovTargetHoverIntents()) {
        return;
      }
      if (isPending) {
        trailingScheduleRequested = true;
        return;
      }
      startWait(generation);
    },

    invalidate(): void {
      generation += 1;
      trailingScheduleRequested = false;
    },
  };
}

export function schedulePostPaintHovTargetHoverRefresh(
  options: SchedulePostPaintHovTargetHoverRefreshOptions,
): void {
  createPostPaintHovTargetHoverRefreshScheduler(options).schedule();
}
